import React from "react";

import { MultiSelectDropdown, Option } from "lib/core_components/MultiSelectDropdownList";
import { assertCurrentUser } from "lib/currentUser";
import { useCompany } from "lib/hooks/api/company/useCompany";
import { UIcon } from "../../lib/icons/untitled_ui/UIcon";
import { sysPrimaryDefault } from "../../lib/themes/colors";
import { Flex, Text } from "../../styles/utility-components";
import UserInitials, { UnassignedIcon } from "../comments/UserInitials";

export type AssigneeFilterValue = { ids: string[]; includeNulls?: boolean };
type User = { guid: string; firstName: string; lastName: string };

const NO_ASSIGNEE_VALUE = "noAssignee";

const noAssigneeOption = {
  title: NO_ASSIGNEE_VALUE,
  value: NO_ASSIGNEE_VALUE,
  OptionComponent: (
    <Flex gap={9} alignItems="center" minWidth={200} height={37}>
      <UnassignedIcon showTooltip={false} type="extra-small" />
      <Text fontWeight="500">No assignee</Text>
    </Flex>
  ),
};

function generateAssigneeOptions(
  users: { guid: string; firstName: string; lastName: string }[],
): Option<string>[] {
  const userOptions = users.map((user) => ({
    title: `${user.firstName} ${user.lastName}`,
    value: user.guid,
    OptionComponent: (
      <Flex gap={9} alignItems="center" key={user.guid} minWidth={200} height={37}>
        <UserInitials
          firstName={user?.firstName}
          lastName={user?.lastName}
          showTooltip={false}
          type="extra-small"
        />
        <Text fontWeight="500">{`${user.firstName} ${user.lastName}`}</Text>
      </Flex>
    ),
  }));

  const userId = window.currentUser?.guid ?? "";
  // bring the current user to the top of the list and unassigned to the second in the list
  const currentUser = userOptions.find((user) => user.value === userId);

  if (!currentUser) {
    return [noAssigneeOption, ...userOptions];
  }

  return [currentUser, noAssigneeOption, ...userOptions.filter((user) => user.value !== userId)];
}

type Props = {
  onChange: (v: AssigneeFilterValue) => void;
  value: AssigneeFilterValue;
};
export function AssigneeDropdownFilter(props: Props) {
  const currentUser = assertCurrentUser();

  const { data: company, isLoading: isLoadingCompany } = useCompany();

  const onChange = (values: string[]) => {
    const selectedAssignees = values.filter((assignee) => assignee !== NO_ASSIGNEE_VALUE);
    const includeUnassigned = !!values.find((assignee) => assignee === NO_ASSIGNEE_VALUE);
    return props.onChange({
      ids: selectedAssignees,
      includeNulls: includeUnassigned,
    });
  };

  const value = [...props.value.ids, ...(props.value.includeNulls ? [NO_ASSIGNEE_VALUE] : [])];

  const companyUsers = company?.users ?? [];

  return (
    <MultiSelectDropdown
      title={
        <Flex gap={8} alignItems="center">
          <UIcon icon="user01" color={sysPrimaryDefault} />
          {getSelectDropdownLabel(props.value, companyUsers, currentUser.guid)}
        </Flex>
      }
      fieldName="assignee"
      searchable
      hideSelectAll
      skipSort
      options={generateAssigneeOptions(companyUsers)}
      selectedOptions={value}
      onChange={onChange}
      loading={isLoadingCompany}
      showSelectedSizeBadge={false}
    />
  );
}

function getSelectDropdownLabel(
  selectedValue: AssigneeFilterValue,
  allUsers: User[],
  currentUserId: string,
): string {
  const { ids, includeNulls: unassignedSelected } = selectedValue;

  const numSelectedAssignees = ids.length;
  const totalUsers = allUsers.length;

  const getAssigneeFullNameById = (id: string) => {
    const user = allUsers.find((u) => u.guid === id);
    return user ? `${user.firstName} ${user.lastName}` : null;
  };

  if (unassignedSelected) {
    if (numSelectedAssignees === 0) return "No assignee";
    if (numSelectedAssignees < totalUsers) return `${numSelectedAssignees + 1} assignees`;
    return "Assigned to anyone";
  }

  if (numSelectedAssignees === 0) return "Assigned to anyone";

  if (numSelectedAssignees === 1) {
    return ids[0] === currentUserId
      ? "Assigned to me"
      : `Assigned to ${getAssigneeFullNameById(ids[0])}`;
  }

  if (numSelectedAssignees < totalUsers) {
    return `${numSelectedAssignees} assignees`;
  }

  return "Assigned to anyone";
}
